import { PlusIcon, XIcon } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { cn } from '../../../lib/utils.ts';

type Option<TValue = string> = {
    value: TValue;
    label: string;
    subtitle?: string;
};

type CreatableMultiSelectProps<TValue = string> = {
    options?: Option<TValue>[];
    value: TValue[];
    onChange: (value: TValue[]) => void;
    placeholder?: string;
    disabled?: boolean;
    variant?: 'default' | 'inline';
    className?: string;
    isValidNewOption: (inputValue: string) => boolean;
    onCreateOption: (inputValue: string) => TValue;
    onInvalidOption: (inputValue: string) => void;
    formatOptionLabel?: (option: Option<TValue>) => React.ReactNode;
    formatValueLabel?: (value: TValue, option: Option<TValue> | undefined) => React.ReactNode;
    filterOption?: (option: Option<TValue>, inputValue: string) => boolean;
    maxSuggestions?: number;
    errorMessage?: string;
};

function defaultFilterOption<TValue>(option: Option<TValue>, inputValue: string): boolean {
    const query = inputValue.toLowerCase();
    return (
        option.label.toLowerCase().includes(query) ||
        (option.subtitle?.toLowerCase().includes(query) ?? false)
    );
}

function renderDefaultOptionLabel<TValue>(option: Option<TValue>): React.ReactNode {
    return (
        <div className="flex flex-col">
            <Text size="sm" color="neutral-950">
                {option.label}
            </Text>
            {option.subtitle && (
                <Text size="xs" color="neutral-500">
                    {option.subtitle}
                </Text>
            )}
        </div>
    );
}

function renderDefaultValueLabel<TValue>(val: TValue, option: Option<TValue> | undefined): React.ReactNode {
    if (option?.subtitle) {
        return `${option.label} (${option.subtitle})`;
    }
    return option?.label ?? String(val);
}

export function CreatableMultiSelect<TValue = string>({
    options = [],
    value,
    onChange,
    placeholder,
    disabled,
    variant = 'default',
    className,
    isValidNewOption,
    onCreateOption,
    onInvalidOption,
    formatOptionLabel,
    formatValueLabel,
    filterOption = defaultFilterOption,
    maxSuggestions = 5,
    errorMessage,
}: CreatableMultiSelectProps<TValue>): React.JSX.Element {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    const filteredOptions = useMemo(() => {
        if (!inputValue.trim()) {
            return [];
        }
        return options
            .filter((option) => !value.includes(option.value))
            .filter((option) => filterOption(option, inputValue))
            .slice(0, maxSuggestions);
    }, [options, inputValue, value, filterOption, maxSuggestions]);

    const optionsByValue = useMemo(() => {
        const map = new Map<TValue, Option<TValue>>();
        for (const option of options) {
            map.set(option.value, option);
        }
        return map;
    }, [options]);

    const addOption = useCallback(
        (val: TValue): void => {
            if (!value.includes(val)) {
                onChange([...value, val]);
            }
            setInputValue('');
            setShowSuggestions(false);
        },
        [value, onChange],
    );

    const removeValue = useCallback(
        (val: TValue): void => {
            onChange(value.filter((v) => v !== val));
        },
        [value, onChange],
    );

    const submitInput = useCallback((): void => {
        const trimmed = inputValue.trim();
        if (!trimmed) {
            return;
        }

        if (!isValidNewOption(trimmed)) {
            onInvalidOption(trimmed);
            return;
        }

        addOption(onCreateOption(trimmed));
    }, [inputValue, isValidNewOption, onInvalidOption, onCreateOption, addOption]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            submitInput();
        }
    };

    const renderOptionLabel = formatOptionLabel ?? renderDefaultOptionLabel;
    const renderValueLabel = formatValueLabel ?? renderDefaultValueLabel;

    return (
        <div className={cn('flex w-full flex-col gap-2', className)}>
            <div className="relative flex items-center gap-1">
                <Input
                    ref={inputRef}
                    variant={variant}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setInputValue(e.target.value);
                        setShowSuggestions(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setShowSuggestions(true);
                    }}
                    onBlur={(e) => {
                        // We need to check if the new focused element is within our suggestions dropdown, otherwise we close it
                        if (
                            suggestionsRef.current?.contains(
                                e.relatedTarget instanceof Node ? e.relatedTarget : null,
                            )
                        ) {
                            return;
                        }

                        setShowSuggestions(false);
                    }}
                    disabled={disabled}
                />

                <Button
                    variant="ghost"
                    icon={PlusIcon}
                    size="icon"
                    className="shrink-0 p-0"
                    onClick={submitInput}
                />

                {showSuggestions && filteredOptions.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        className="absolute top-full left-0 z-50 mt-1 w-full rounded-md border border-neutral-200 bg-white shadow-md"
                    >
                        {filteredOptions.map((option) => (
                            <button
                                key={String(option.value)}
                                type="button"
                                className="flex w-full flex-col px-3 py-2 text-left hover:bg-neutral-50"
                                onClick={() => {
                                    addOption(option.value);
                                    inputRef.current?.focus();
                                }}
                            >
                                {renderOptionLabel(option)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {!!errorMessage && (
                <Text size="xs" color="red-600">
                    {errorMessage}
                </Text>
            )}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((val) => (
                        <span
                            key={String(val)}
                            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700"
                        >
                            {renderValueLabel(val, optionsByValue.get(val))}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        removeValue(val);
                                    }}
                                    className="text-neutral-400 hover:text-neutral-600"
                                >
                                    <XIcon className="h-3 w-3" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export type { Option as CreatableMultiSelectOption, CreatableMultiSelectProps };
